import React from 'react';
import {
  Shield, RefreshCw, CheckCircle, HelpCircle, Clock, ChevronDown, Loader2
} from 'lucide-react';

export interface GovernanceData {
  useCaseId: string;
  riskClassification: { level: number; label: string };
  surveys: Array<{
    surveyId: string;
    surveyName: string;
    questions: Array<{ questionId: string; question: string; answer: string }>;
  }>;
}

interface GovernancePanelProps {
  data: GovernanceData | null;
  loading: boolean;
  hasTranscript: boolean;
  isReadOnly: boolean;
  expandedSurveys: Set<string>;
  onSync: () => void;
  onToggleSurvey: (surveyId: string) => void;
}

const riskStyles = (level: number) => {
  switch (level) {
    case 3: return { border: 'border-l-red-500', text: 'text-red-600', badge: 'bg-red-100 text-red-700' };
    case 2: return { border: 'border-l-amber-500', text: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' };
    default: return { border: 'border-l-emerald-500', text: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-700' };
  }
};

export function GovernancePanel({
  data,
  loading,
  hasTranscript,
  isReadOnly,
  expandedSurveys,
  onSync,
  onToggleSurvey,
}: GovernancePanelProps) {
  const disabled = loading || !hasTranscript || isReadOnly;

  // Empty state
  if (!data && !loading) {
    return (
      <div className="flex-1 overflow-y-auto p-8">
        <div className="w-full flex flex-col gap-6">
          <header className="flex items-center justify-between border-b border-slate-200 pb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                <Shield size={22} />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-navy">AI Governance</h1>
                <p className="text-slate-500 text-sm font-medium">Credo.ai use case management</p>
              </div>
            </div>
          </header>
          {/* Empty state card matching Stitch design */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-16 flex flex-col items-center justify-center text-center">
            <div className="w-full max-w-2xl border-2 border-dashed border-primary/20 rounded-xl p-12 flex flex-col items-center gap-8">
              <div className="h-24 w-24 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield size={48} className="text-primary" />
              </div>
              <div className="flex flex-col items-center gap-3 max-w-md">
                <h2 className="text-2xl font-bold text-navy tracking-tight">
                  No governance data yet
                </h2>
                <p className="text-slate-500 text-base leading-relaxed">
                  {hasTranscript
                    ? 'Click Sync with Credo.ai to create a use case from your session transcript and ensure your AI models are compliant.'
                    : 'Start the workshop conversation to enable AI Governance sync with Credo.ai.'}
                </p>
              </div>
              {hasTranscript && !isReadOnly && (
                <button
                  onClick={onSync}
                  className="flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold px-6 py-3 rounded-xl transition-all shadow-lg shadow-primary/20"
                >
                  <RefreshCw size={18} />
                  <span>Sync with Credo.ai</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const rs = data ? riskStyles(data.riskClassification.level) : null;
  const totalQuestions = data?.surveys.reduce((sum, s) => sum + s.questions.length, 0) ?? 0;

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="w-full flex flex-col gap-6">

        {/* Header */}
        <header className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Shield size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">AI Governance</h1>
              <p className="text-slate-500 text-sm font-medium">Credo.ai use case management</p>
            </div>
          </div>
          <button
            onClick={onSync}
            disabled={disabled}
            className="flex items-center justify-center gap-2 rounded-xl h-10 px-5 bg-white border border-slate-200 hover:bg-slate-50 text-navy text-sm font-bold shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
            <span>{loading ? 'Syncing...' : 'Sync with Credo.ai'}</span>
          </button>
        </header>

        {/* Loading state */}
        {loading && !data ? (
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <Loader2 size={32} className="text-primary animate-spin mx-auto mb-4" />
            <p className="text-slate-500 text-sm font-medium">Syncing with Credo.ai...</p>
          </div>
        ) : data ? (
          <>
            {/* Risk Classification Card */}
            <div className={`flex flex-col md:flex-row items-stretch justify-between gap-6 rounded-xl bg-white p-6 shadow-sm border border-slate-200 border-l-[6px] ${rs!.border} relative overflow-hidden`}>
              <div className="flex flex-col gap-3 flex-1 z-10">
                <div className="flex items-center gap-3 mb-1">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${rs!.badge}`}>
                    Level {data.riskClassification.level}
                  </span>
                  <span className="text-slate-400 text-sm font-medium">Use Case ID: {data.useCaseId}</span>
                </div>
                <h2 className={`text-2xl font-bold leading-tight ${rs!.text}`}>
                  {data.riskClassification.label}
                </h2>
                <p className="text-slate-600 text-sm leading-relaxed max-w-xl">
                  {data.riskClassification.level === 3
                    ? 'This use case has been classified as High Risk. Requires immediate review and enhanced governance controls.'
                    : data.riskClassification.level === 2
                    ? 'This use case has been classified as Medium Risk. Requires enhanced oversight and quarterly assessments.'
                    : 'This use case has been classified as Low Risk. Standard governance controls apply.'}
                </p>
              </div>
              <div className="hidden md:flex w-full max-w-[200px] h-28 bg-primary/5 rounded-lg items-center justify-center">
                <Shield size={56} className="text-primary/20" />
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex flex-col gap-1 rounded-xl p-5 bg-white border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={18} className="text-primary" />
                  <p className="text-slate-500 text-sm font-medium">Surveys</p>
                </div>
                <p className="text-navy text-3xl font-bold">{data.surveys.length}</p>
              </div>
              <div className="flex flex-col gap-1 rounded-xl p-5 bg-white border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <HelpCircle size={18} className="text-primary" />
                  <p className="text-slate-500 text-sm font-medium">Questions Answered</p>
                </div>
                <p className="text-navy text-3xl font-bold">{totalQuestions}</p>
              </div>
              <div className="flex flex-col gap-1 rounded-xl p-5 bg-white border border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={18} className="text-primary" />
                  <p className="text-slate-500 text-sm font-medium">Last Synced</p>
                </div>
                <p className="text-navy text-3xl font-bold">Just now</p>
              </div>
            </div>

            {/* Governance Surveys */}
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-end px-1">
                <h2 className="text-xl font-bold text-navy">
                  Governance Surveys
                  <span className="text-sm font-normal text-slate-400 ml-2">({data.surveys.length})</span>
                </h2>
              </div>

              <div className="flex flex-col gap-3">
                {data.surveys.map((survey) => {
                  const isExpanded = expandedSurveys.has(survey.surveyId);
                  return (
                    <div key={survey.surveyId} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                      <button
                        onClick={() => onToggleSurvey(survey.surveyId)}
                        className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                            <Shield size={20} />
                          </div>
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <p className="text-navy text-base font-bold">{survey.surveyName}</p>
                              <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                                {survey.questions.length} questions
                              </span>
                            </div>
                          </div>
                        </div>
                        <ChevronDown
                          size={20}
                          className={`text-slate-400 transition-transform duration-200 shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {isExpanded && (
                        <div className="px-6 pb-6 pt-2 border-t border-slate-100">
                          <div className="space-y-5">
                            {survey.questions.map((qa) => (
                              <div key={qa.questionId} className="flex gap-3">
                                <div className="mt-0.5 shrink-0">
                                  <CheckCircle size={18} className="text-primary" />
                                </div>
                                <div className="flex flex-col gap-1.5 flex-1">
                                  <p className="text-navy text-sm font-bold">{qa.question}</p>
                                  <p className="text-slate-600 text-sm leading-relaxed bg-slate-50 p-3 rounded-lg border border-slate-100">
                                    {qa.answer}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
